import { apiClient } from '../../lib/apiClient';

// Absolute-or-relative base for plain <a href> download links (REACT_APP_BACKEND_URL
// is empty in the single-container build, so these are same-origin paths there).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const formatBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
};

// 0 means "keep forever"; 365 reads better as a year.
export const expiryLabel = (days) => {
  if (days === 0) return 'Keep forever';
  if (days === 365) return '1 year';
  return `${days} day${days === 1 ? '' : 's'}`;
};

// FastAPI returns either a message string or a list of validation errors.
export const apiErrorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
  return fallback;
};

export const getConfig = () => apiClient.get('/transfers/config').then((r) => r.data);

export const getSlides = () => apiClient.get('/transfers/slides').then((r) => r.data);

export const getPage = (slug) => apiClient.get(`/transfers/pages/${encodeURIComponent(slug)}`).then((r) => r.data);

export const getTransfer = (token) => apiClient.get(`/transfers/t/${token}`).then((r) => r.data);

export const unlockTransfer = (token, password) =>
  apiClient.post(`/transfers/t/${token}/unlock`, { password }).then((r) => r.data.ticket);

const withTicket = (url, ticket) => (ticket ? `${url}?ticket=${encodeURIComponent(ticket)}` : url);

export const fileUrl = (token, fileId, ticket) =>
  withTicket(`${BACKEND_URL}/api/transfers/t/${token}/files/${fileId}`, ticket);

export const backgroundUrl = (token) => `${BACKEND_URL}/api/transfers/t/${token}/background`;

export const zipUrl = (token, ticket) => withTicket(`${BACKEND_URL}/api/transfers/t/${token}/zip`, ticket);

export const getMine = () => apiClient.get('/transfers/mine').then((r) => r.data);

export const deleteTransfer = (id, manageToken) =>
  apiClient.delete(`/transfers/${id}`, manageToken ? { headers: { 'X-Manage-Token': manageToken } } : undefined);

/** Origin of the site this page is framed by (the client's site), or null when it cannot be told. */
export const embedParentOrigin = () => {
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length) return ancestors[0];
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
};

const MAX_RETRIES = 3;

/**
 * Upload files in chunks, resuming from the server's acknowledged offset when a
 * chunk fails or arrives out of order. Resolves with the complete() response.
 */
export async function sendTransfer({ entries, options, background, onProgress }) {
  const files = entries.map((e) => e.file);
  const { data: init } = await apiClient.post('/transfers/init', {
    files: entries.map(({ file, path }) => ({ name: file.name, path: path || '', size: file.size })),
    ...options,
  });
  const headers = { 'X-Upload-Token': init.upload_token, 'Content-Type': 'application/octet-stream' };
  let finished = 0; // bytes of fully-uploaded earlier files

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const fileId = init.files[i].id;
    let offset = 0;
    let attempts = 0;
    while (offset < file.size) {
      const end = Math.min(offset + init.chunk_size, file.size);
      try {
        await apiClient.put(`/transfers/${init.transfer_id}/files/${fileId}/chunk`, file.slice(offset, end), {
          params: { offset },
          headers,
          onUploadProgress: (e) => onProgress(finished + offset + e.loaded),
        });
        offset = end;
        attempts = 0;
      } catch (err) {
        if (err.response?.status === 409) {
          offset = err.response.data.received; // server is ahead/behind: resync
        } else if (!err.response || err.response.status >= 500) {
          attempts += 1;
          if (attempts >= MAX_RETRIES) throw err;
        } else {
          throw err;
        }
      }
    }
    finished += file.size;
    onProgress(finished);
  }

  if (background) {
    await apiClient.put(`/transfers/${init.transfer_id}/background`, background, { headers });
  }

  const { data } = await apiClient.post(`/transfers/${init.transfer_id}/complete`, null, {
    headers: { 'X-Upload-Token': init.upload_token },
  });
  return { ...data, transfer_id: init.transfer_id };
}
